import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { VehicleType } from "@api/graphql/generated/generated-types";
import { PackageItemFormData } from "@src/common/components/PackageDimensionsForm/PackageDimensionsForm";
import {
  collectEffectivePackageTypeIds,
  filterVehicleTypesByEffectivePackageTypes,
} from "@src/common/utils/filterVehicleTypesByEffectivePackageTypes";

interface UseFilterVehicleTypesByPackageTypesParams {
  vehicleTypes: VehicleType[] | undefined;
  vehicleTypeFieldName: string;
  packageTypeFieldName: string;
  packageItemsFieldName: string;
}

/**
 * Narrows an already service-filtered vehicle list so each offered vehicle
 * allows every effective package type in play (shipment type + item overrides).
 * Clears the selected vehicle when it becomes invalid.
 */
export function useFilterVehicleTypesByPackageTypes({
  vehicleTypes,
  vehicleTypeFieldName,
  packageTypeFieldName,
  packageItemsFieldName,
}: UseFilterVehicleTypesByPackageTypesParams): {
  visibleVehicleTypes: VehicleType[];
} {
  const { getValues, setValue } = useFormContext();
  const shipmentPackageTypeId = useWatch({
    name: packageTypeFieldName,
  }) as string | null | undefined;
  const packageItems = useWatch({
    name: packageItemsFieldName,
  }) as PackageItemFormData[] | undefined;

  const effectivePackageTypeIds: string[] = useMemo(
    () => collectEffectivePackageTypeIds(shipmentPackageTypeId, packageItems),
    [shipmentPackageTypeId, packageItems],
  );

  const visibleVehicleTypes: VehicleType[] = useMemo(() => {
    if (!vehicleTypes) {
      return [];
    }
    return filterVehicleTypesByEffectivePackageTypes(
      vehicleTypes,
      effectivePackageTypeIds,
    );
  }, [vehicleTypes, effectivePackageTypeIds]);

  useEffect(() => {
    if (!vehicleTypes) {
      return;
    }
    const currentVehicleTypeId = getValues(vehicleTypeFieldName) as
      | string
      | undefined;
    if (!currentVehicleTypeId) {
      return;
    }
    const isStillVisible = visibleVehicleTypes.some(
      (vehicleType) => vehicleType.id === currentVehicleTypeId,
    );
    if (isStillVisible) {
      return;
    }
    setValue(vehicleTypeFieldName, undefined, { shouldDirty: true });
    // Key on the package-type constraint and the filtered list — not on the
    // field value — so explicit deselection is not fought.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePackageTypeIds, visibleVehicleTypes, vehicleTypes]);

  return { visibleVehicleTypes };
}
